import { type DayNumber, type ISODate, fromDay, toDay, weekdayOfDay } from "./date";
import { resolveCalendar } from "./holiday-catalogs";
import type { HolidaySuppression, UserHoliday } from "./holiday-catalogs";

/**
 * Working-day calendar.
 *
 * All schedule arithmetic happens in "working ordinal" space: an integer that counts working
 * days from an epoch. Converting to and from calendar dates is done here, and only here.
 *
 *  - `ordinalOf(day)`  → ordinal of the first working day on or after `day` (snap forward)
 *  - `ordinalBefore(day)` → ordinal of the last working day on or before `day` (snap backward)
 *  - `dayOf(ordinal)` → the calendar day for that working ordinal
 *
 * A task with start ordinal `s` and duration `d` (working days) occupies ordinals [s, s+d),
 * so its display finish date is `dayOf(s + d - 1)`. A milestone (d = 0) is at `dayOf(s)`.
 */
export interface CalendarDefinition {
  /** index 0 = Sunday … 6 = Saturday */
  workingDays: [boolean, boolean, boolean, boolean, boolean, boolean, boolean];
  /**
   * Dates that remove a working day. Kept in sync with the catalogs and additions below
   * so schedule math can stay a flat set.
   */
  holidays: ISODate[];
  /** extra working dates that override the weekly pattern */
  workingExceptions?: ISODate[];
  /** Selected holiday calendars. Absent on older saves; load migrates those. */
  holidayCatalogs?: string[];
  /** Plan-authored holidays. Disabled ones stay listed and do not affect the schedule. */
  userHolidays?: UserHoliday[];
  /** Official days turned off while their calendar stays selected. */
  holidaySuppressions?: HolidaySuppression[];
}

export const DEFAULT_CALENDAR: CalendarDefinition = {
  workingDays: [false, true, true, true, true, true, false],
  holidays: [],
};

export { KR_HOLIDAYS_2026, KR_HOLIDAYS_2027, calendarWithKoreanHolidays } from "./holiday-catalogs";

const EPOCH_DAY = toDay("2015-01-01");

export class WorkCalendar {
  readonly def: CalendarDefinition;
  private readonly holidaySet: Set<DayNumber>;
  private readonly workingExceptionSet: Set<DayNumber>;
  /** ordinal → dayNumber, cached forward from EPOCH_DAY */
  private ordinalDays: DayNumber[] = [];
  /** dayNumber - EPOCH_DAY → ordinal of first working day ≥ that day */
  private dayOrdinals: Int32Array = new Int32Array(0);
  private cachedUntilDay: DayNumber = EPOCH_DAY - 1;
  readonly workingDaysPerWeek: number;

  constructor(def: CalendarDefinition = DEFAULT_CALENDAR) {
    const resolved = resolveCalendar(def);
    this.def = resolved;
    this.holidaySet = new Set(resolved.holidays.map(toDay));
    this.workingExceptionSet = new Set((def.workingExceptions ?? []).map(toDay));
    this.workingDaysPerWeek = def.workingDays.filter(Boolean).length || 1;
  }

  isWorkingDay(day: DayNumber): boolean {
    if (this.workingExceptionSet.has(day)) return true;
    if (this.holidaySet.has(day)) return false;
    return this.def.workingDays[weekdayOfDay(day)] ?? false;
  }

  isWorkingISO(iso: ISODate): boolean {
    return this.isWorkingDay(toDay(iso));
  }

  private ensure(day: DayNumber) {
    if (day <= this.cachedUntilDay) return;
    // Grow in chunks to amortise allocation.
    const target = Math.max(day, this.cachedUntilDay + 400);
    const oldLen = this.dayOrdinals.length;
    const newLen = target - EPOCH_DAY + 1;
    const next = new Int32Array(newLen);
    next.set(this.dayOrdinals);
    for (let d = this.cachedUntilDay + 1; d <= target; d++) {
      if (this.isWorkingDay(d)) this.ordinalDays.push(d);
      // ordinal of first working day ≥ d: if d is working, it's the one just pushed.
      next[d - EPOCH_DAY] = this.isWorkingDay(d) ? this.ordinalDays.length - 1 : this.ordinalDays.length;
    }
    void oldLen;
    this.dayOrdinals = next;
    this.cachedUntilDay = target;
  }

  /** ordinal of first working day ≥ day */
  ordinalOf(day: DayNumber): number {
    if (day < EPOCH_DAY) day = EPOCH_DAY;
    this.ensure(day + 14);
    return this.dayOrdinals[day - EPOCH_DAY]!;
  }

  /** ordinal of last working day ≤ day */
  ordinalBefore(day: DayNumber): number {
    if (day < EPOCH_DAY) day = EPOCH_DAY;
    this.ensure(day + 14);
    const o = this.dayOrdinals[day - EPOCH_DAY]!;
    return this.isWorkingDay(day) ? o : Math.max(0, o - 1);
  }

  ordinalOfISO(iso: ISODate): number {
    return this.ordinalOf(toDay(iso));
  }

  ordinalBeforeISO(iso: ISODate): number {
    return this.ordinalBefore(toDay(iso));
  }

  dayOf(ordinal: number): DayNumber {
    if (ordinal < 0) ordinal = 0;
    while (ordinal >= this.ordinalDays.length) {
      this.ensure(this.cachedUntilDay + 400);
    }
    return this.ordinalDays[ordinal]!;
  }

  isoOf(ordinal: number): ISODate {
    return fromDay(this.dayOf(ordinal));
  }

  /** Number of working days in the inclusive date range. */
  workingDaysBetween(startISO: ISODate, endISO: ISODate): number {
    const a = toDay(startISO);
    const b = toDay(endISO);
    if (b < a) return 0;
    return this.ordinalBefore(b) - this.ordinalOf(a) + 1;
  }

  /**
   * Working days from `statusDate` to `finish`. Same day is 0; past the finish is negative.
   * Weekend/holiday dates snap to the last working day on or before that date.
   */
  remainingWorkingDays(statusDate: ISODate, finish: ISODate): number {
    return this.ordinalBeforeISO(finish) - this.ordinalBeforeISO(statusDate);
  }

  /** Add `n` working days to a date (n may be negative). Result is a working day. */
  addWorkingDays(iso: ISODate, n: number): ISODate {
    return this.isoOf(this.ordinalOfISO(iso) + n);
  }
}
